import { FC, Suspense, useState } from 'react';
import { capitalize } from 'lodash';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import AvatarGroup from '@mui/material/AvatarGroup';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import ArchiveOutlined from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlined from '@mui/icons-material/UnarchiveOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { Row, Column } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import BasicSelect from 'further-ui/components/BasicSelect';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import { Country, AmlMatchTypeLabels, countries } from 'further-ui/labels';
import { CalendarDay, pluralizedCount } from 'further-ui/utils';
import { AmlMatchType } from 'further-types/aml';
import { usePagination } from 'further-ui/hooks';
import {
  formatTypeOfFailureString,
  furtherCased,
} from 'helpers/kyc/text-formatting';
import AmlResultInformation from './AmlResultInformation';
import ReviewButton from '../ReviewButton';
import { Box } from '@mui/material';
import { SortOrder } from 'further-types/utils';

enum AmlResultStatus {
  All = 'all',
  Archived = 'archived',
  Unarchived = 'unarchived',
}

const ExpandedContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.sm),
}));

const CountryAvatarGroup = styled(AvatarGroup)(({ theme }) => ({
  justifyContent: 'flex-end',
  '& > .MuiAvatar-colorDefault': {
    width: 24,
    height: 24,
    fontSize: 10,
    fontWeight: 500,
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.grey[900],
  },
  '--AvatarGroup-spacing': '-4px !important',
}));

const CountryAvatar = styled(Avatar)(({ theme }) => ({
  width: 24,
  height: 24,
  border: `1px solid ${theme.palette.grey[300]}`,
}));

const Unarchive = styled(UnarchiveOutlined, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled: boolean }>(({ theme, disabled }) => ({
  color: disabled ? 'disabled' : theme.palette.success.light,
}));

const Archive = styled(ArchiveOutlined, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled: boolean }>(({ theme, disabled }) => ({
  color: disabled ? 'disabled' : theme.palette.error.light,
}));

const AmlResultStatusSelect = styled(BasicSelect<AmlResultStatus>)({
  width: 192,
});

type Props = {
  vehicleId: string;
  investorId: string;
  investorKyc: {
    amlPotentialMatches: Array<{
      id: string;
      createdAt?: string;
      type: string;
      value: string;
      reasons: Array<AmlMatchType>;
      countryNames: Array<Country>;
      archived: boolean;
      caDocId: string;
    }>;
  } | null;
  archiveAmlHit: (caDocId: string) => void;
  unarchiveAmlHit: (caDocId: string) => void;
  isPending: boolean;
};

const AmlResult: FC<Props> = ({
  vehicleId,
  investorId,
  investorKyc,
  archiveAmlHit,
  unarchiveAmlHit,
  isPending,
}) => {
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [showAmlResults, setShowAmlResults] = useState(false);
  const [amlResultStatus, setAmlResultStatus] = useState<AmlResultStatus>(
    AmlResultStatus.Unarchived,
  );
  const pagination = usePagination({
    id: 'investor-journey-aml-results',
    orderBy: 'createdAt',
    order: SortOrder.Desc,
  });

  const amlPotentialMatches = investorKyc?.amlPotentialMatches ?? [];
  const potentialMatchCount = amlPotentialMatches.length;
  const potentialUnarchivedMatches = amlPotentialMatches.filter(
    (match) => !match.archived,
  );
  const potentialArchivedMatches = amlPotentialMatches.filter(
    (match) => match.archived,
  );
  const rows =
    amlResultStatus === AmlResultStatus.All
      ? amlPotentialMatches
      : amlResultStatus === AmlResultStatus.Archived
      ? potentialArchivedMatches
      : potentialUnarchivedMatches;

  const amlResult =
    potentialUnarchivedMatches.length === 0
      ? `No potential matches${
          potentialArchivedMatches.length > 0
            ? `. ${pluralizedCount(
                'hit',
                potentialArchivedMatches.length,
                'hits',
              )} archived.`
            : ''
        }`
      : pluralizedCount(
          'potential match',
          potentialUnarchivedMatches.length,
          'potential matches',
        );

  const columns: Array<TableColumn<(typeof amlPotentialMatches)[number]>> = [
    {
      label: 'Date',
      key: 'createdAt',
      disableSorting: true,
      render: (match) =>
        match.createdAt ? new CalendarDay(match.createdAt).toReadable() : '--',
    },
    {
      label: 'Hit name',
      key: 'value',
      disableSorting: true,
    },
    {
      label: 'Type',
      key: 'type',
      disableSorting: true,
      render: (match) => formatTypeOfFailureString(match.type),
    },
    {
      label: 'Reason',
      key: 'reasons',
      disableSorting: true,
      render: (match) =>
        match.reasons
          .map((reason) => AmlMatchTypeLabels[reason])
          .map(furtherCased)
          .join(', '),
    },
    {
      label: 'Countries',
      key: 'countryNames',
      disableSorting: true,
      noWrap: true,
      paddingY: 0,
      width: '1%',
      render: (match) => {
        const countryCount = match.countryNames.length;
        const tooltipTitle =
          countryCount > 1
            ? `${match.countryNames
                .slice(0, countryCount - 1)
                .join(', ')} and ${match.countryNames[countryCount - 1]}`
            : countryCount === 1
            ? match.countryNames[0]
            : '--';

        return (
          <Tooltip title={tooltipTitle} placement="top">
            <CountryAvatarGroup max={3} total={countryCount}>
              {match.countryNames.map((country) => {
                const countryCode = countries
                  .find((c) => c.name === country)
                  ?.code.toLowerCase();

                return countryCode ? (
                  <CountryAvatar
                    key={country}
                    alt={country}
                    src={`https://flagcdn.com/w80/${countryCode}.png`}
                  />
                ) : null;
              })}
            </CountryAvatarGroup>
          </Tooltip>
        );
      },
    },
    {
      key: 'actions',
      noWrap: true,
      paddingY: 0,
      width: '1%',
      render: (match) => (
        <Row centered>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              if (match.archived) {
                unarchiveAmlHit(match.caDocId);
              } else {
                archiveAmlHit(match.caDocId);
              }
            }}
            disabled={isPending}
          >
            {match.archived ? (
              <Unarchive disabled={isPending} />
            ) : (
              <Archive disabled={isPending} />
            )}
          </IconButton>
          <IconButton
            onClick={() => {
              setExpandedRowId(expandedRowId === match.id ? null : match.id);
            }}
          >
            {expandedRowId === match.id ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Row>
      ),
    },
  ];

  return (
    <Column spacing="xs">
      <Row justify="space-between">
        <FormField label="AML result" spacing="xxs">
          <Row spacing="xs">
            <Typography variant="body2" color="grey.600">
              {investorKyc ? amlResult : '--'}
            </Typography>
            {potentialMatchCount !== 0 && (
              <ReviewButton
                variant="text"
                onClick={() => {
                  setShowAmlResults(!showAmlResults);
                }}
              >
                {showAmlResults ? 'Hide results' : 'Show results'}
              </ReviewButton>
            )}
          </Row>
        </FormField>
        {showAmlResults ? (
          <Row centered spacing="sm">
            <Typography variant="h6" color="grey.500">
              Status:
            </Typography>
            <AmlResultStatusSelect
              value={amlResultStatus}
              onChange={setAmlResultStatus}
              options={Object.values(AmlResultStatus).map((status) => ({
                label: capitalize(status),
                value: status,
              }))}
            />
          </Row>
        ) : null}
      </Row>
      {showAmlResults ? (
        <Table
          columns={columns}
          rows={rows}
          rowsTotalCount={rows.length}
          isExpandedRow={(row) => row.id === expandedRowId}
          getExpandedContent={(row) => (
            <Suspense fallback={<CircularProgress size={20} />}>
              <ExpandedContentContainer>
                <AmlResultInformation
                  vehicleId={vehicleId}
                  investorId={investorId}
                  caDocId={row.caDocId}
                />
              </ExpandedContentContainer>
            </Suspense>
          )}
          onRowClick={(row) => {
            setExpandedRowId(expandedRowId === row.id ? null : row.id);
          }}
          pagination={pagination}
          disableSorting
          emptyMessage="No potential matches found"
        />
      ) : null}
    </Column>
  );
};

export default AmlResult;
